import React from "react";
import { Box, Chip, Typography, CardContent } from "@mui/material";
import { StyledCard } from "../styled";

const FIELD_ORDER = [
  "bar_name",
  "address",
  "bar_type",
  "description",
  "phone",
  "website",
  "email",
  "cuisine",
  "wifi",
  "outdoor_seating"
];

const ConflictsTable = ({ conflicts, fieldSelections, onFieldSelection }) => (
  <StyledCard>
    <CardContent>
      <Box
        sx={{
          overflowX: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: "8px"
        }}
      >
        <Box
          sx={{
            maxHeight: "700px",
            overflowY: "auto"
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "40px 180px 150px repeat(10, 180px)",
              gap: "1px",
              backgroundColor: "#f5f5f5",
              minWidth: "2270px"
            }}
          >
            <HeaderCell>#</HeaderCell>
            <HeaderCell>Match Info</HeaderCell>
            <HeaderCell>Source</HeaderCell>
            {FIELD_ORDER.map((header) => (
              <HeaderCell key={header}>{formatHeader(header)}</HeaderCell>
            ))}

            {conflicts.map((conflict, index) => (
              <React.Fragment key={conflict.id}>
                <StickyCell gridRow="span 2">{index + 1}</StickyCell>

                <StickyInfoCell>
                  <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                    {conflict.osmRecord.bar_name || "Unnamed"}
                  </Typography>
                  <Chip label={`Match: ${conflict.matchReason}`} color="warning" size="small" />
                </StickyInfoCell>

                <SourceCell variant="new">New OSM</SourceCell>
                {FIELD_ORDER.map((fieldName) =>
                  renderFieldCell({
                    conflict,
                    fieldName,
                    fieldSelections,
                    onFieldSelection,
                    source: "new"
                  })
                )}

                <SourceCell variant="existing" bottomBorder={index !== conflicts.length - 1}>
                  Existing DB
                </SourceCell>
                {FIELD_ORDER.map((fieldName) =>
                  renderFieldCell({
                    conflict,
                    fieldName,
                    fieldSelections,
                    onFieldSelection,
                    source: "existing",
                    isLastRow: index === conflicts.length - 1
                  })
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Box>
    </CardContent>
  </StyledCard>
);

const renderFieldCell = ({
  conflict,
  fieldName,
  fieldSelections,
  onFieldSelection,
  source,
  isLastRow = false
}) => {
  const field = conflict.fields[fieldName];
  if (!field) {
    return (
      <ContentCell
        key={`${conflict.id}-${source}-${fieldName}`}
        bottomBorder={!isLastRow}
        background={source === "new" ? "#e3f2fd" : "#c8e6c9"}
      >
        —
      </ContentCell>
    );
  }

  const selectedSource = fieldSelections[conflict.id]?.[fieldName];
  const hasConflict = field.hasConflict;
  const isSelected = selectedSource === source;
  const isUnresolved = hasConflict && !selectedSource;

  const background = source === "new" ? "#e3f2fd" : "#c8e6c9";
  const hoverBackground = source === "new" ? "#a5d6a7" : "#a5d6a7";
  const unresolvedHover = "#ffb3ba";

  const value = source === "new" ? field.new : field.existing;

  return (
    <ContentCell
      key={`${conflict.id}-${source}-${fieldName}`}
      onClick={() => hasConflict && onFieldSelection(conflict.id, fieldName, source)}
      background={
        isSelected && hasConflict ? "#c8e6c9" : isUnresolved ? "#ffcdd2" : background
      }
      hoverBackground={hasConflict ? (isSelected ? hoverBackground : unresolvedHover) : undefined}
      border={isSelected && hasConflict ? "2px solid #4caf50" : isUnresolved ? "2px solid #f44336" : "none"}
      bottomBorder={!isLastRow}
      cursor={hasConflict ? "pointer" : "default"}
    >
      <Typography variant="body2" sx={{ fontSize: "0.85rem", wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </ContentCell>
  );
};

const formatHeader = (header) => {
  if (header === "bar_name") return "Name";
  if (header === "bar_type") return "Type";
  return header
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const HeaderCell = ({ children }) => (
  <Box
    sx={{
      p: 2,
      backgroundColor: "white",
      fontWeight: "bold",
      borderBottom: "2px solid #e0e0e0",
      position: "sticky",
      top: 0,
      zIndex: 10
    }}
  >
    {children}
  </Box>
);

const StickyCell = ({ children, gridRow }) => (
  <Box
    sx={{
      p: 2,
      backgroundColor: "white",
      borderBottom: "1px solid #e0e0e0",
      gridRow,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold"
    }}
  >
    {children}
  </Box>
);

const StickyInfoCell = ({ children }) => (
  <Box
    sx={{
      p: 2,
      backgroundColor: "white",
      borderBottom: "1px solid #e0e0e0",
      gridRow: "span 2",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center"
    }}
  >
    {children}
  </Box>
);

const SourceCell = ({ children, variant, bottomBorder = true }) => {
  const backgroundColor = variant === "new" ? "#e3f2fd" : "#c8e6c9";

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor,
        borderBottom: bottomBorder ? "2px solid #333" : "none",
        fontWeight: "bold",
        display: "flex",
        alignItems: "center"
      }}
    >
      {children}
    </Box>
  );
};

const ContentCell = ({
  children,
  background,
  hoverBackground,
  border,
  bottomBorder = true,
  cursor = "default",
  onClick
}) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2,
      backgroundColor: background,
      borderBottom: bottomBorder ? "1px solid #e0e0e0" : "none",
      cursor,
      transition: "background-color 0.2s",
      border,
      ...(hoverBackground && cursor === "pointer"
        ? {
            "&:hover": {
              backgroundColor: hoverBackground
            }
          }
        : {})
    }}
  >
    {children}
  </Box>
);

export default ConflictsTable;
